
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';

interface RecentUsersProps {
  className?: string;
  selectedGym: string;
}

export function RecentUsers({ className, selectedGym }: RecentUsersProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym && selectedGym !== 'all',
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchIntervalInBackground: true,
  });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{t('dashboard.recentUsers')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-muted animate-pulse"></div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                  <div className="h-3 w-32 bg-muted animate-pulse rounded"></div>
                </div>
                <div className="h-3 w-16 bg-muted animate-pulse rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const recentUsers = data?.recentUsers || [];

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{t('dashboard.recentUsers')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recentUsers.length > 0 ? (
            recentUsers.map((user, index) => (
              <div key={index} className="flex items-center gap-4">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarFallback>
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-sm font-medium leading-none truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {user.joinDate}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              {t('dashboard.noRecentUsers')}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
